import { UserTypes } from 'store/auth/types'
import { AuthenticatedUserTypes, AuthTokens, STORAGE_TYPES, StorageTypes } from 'types/auth'
import { clearSessionUserType, getUserType, identifyUserType, setUserType } from 'utils/userType'
import dayjs from 'dayjs'
import { Token } from '@boltenergy-be/shared'

/**
 * Clears only the sessionStorage (magic-link) tokens for a user type, leaving the localStorage login session
 * and other tabs untouched.
 * @param {UserTypes} userType
 */
const clearSessionTokens = (userType: UserTypes) => {
  sessionStorage.removeItem(`access_token_${userType}`)
  sessionStorage.removeItem(`refresh_token_${userType}`)
}

/**
 * Clears the sessionStorage (magic-link) tokens and session user type for a user type
 * @param {UserTypes} userType
 */
const clearSession = (userType: UserTypes) => {
  clearSessionTokens(userType)
  clearSessionUserType()
}

/**
 * Clears the tokens in localStorage, sets is_authenticated to false for a user type and remove is_superuser when logged in as superuser.
 * @param {UserTypes} userType
 */
const clearLocalTokens = (userType: UserTypes) => {
  if (userType === UserTypes.SUPER_USER) {
    localStorage.removeItem('is_superuser')
  }

  setIsAuthenticated(false, userType)
  localStorage.removeItem(`access_token_${userType}`)
  localStorage.removeItem(`refresh_token_${userType}`)
}

/**
 * Clears the tokens in localStorage and session storage and other relevant local storage items for a user type, effectively logging out the user in all tabs.
 * @param {UserTypes | null} userType
 */
const clearTokens = (userType: UserTypes | null) => {
  if (userType) {
    clearLocalTokens(userType)
    clearSession(userType)
  }
}

/**
 * Checks if the tokens are still valid for a user type
 * @param {UserTypes} userType
 * @returns {boolean}
 */
const checkTokenIsValid = (userType: UserTypes): boolean => {
  // Raw tokens
  const localAccessTokenJSON = localStorage.getItem(`access_token_${userType}`)
  const sessionAccessTokenJSON = sessionStorage.getItem(`access_token_${userType}`)
  const refreshTokenJSON = localStorage.getItem(`refresh_token_${userType}`)

  // No token stored
  if (!sessionAccessTokenJSON && !localAccessTokenJSON && !refreshTokenJSON) return false

  // Parsed tokens
  const localAccessToken: Token | undefined = localAccessTokenJSON ? JSON.parse(localAccessTokenJSON) : undefined
  const sessionAccessToken: Token | undefined = sessionAccessTokenJSON ? JSON.parse(sessionAccessTokenJSON) : undefined
  const refreshToken: Token | undefined = refreshTokenJSON ? JSON.parse(refreshTokenJSON) : undefined

  // No parsed token stored
  if (!sessionAccessToken && !localAccessToken && !refreshToken) return false

  const mostRecentAccessToken =
    sessionAccessToken && localAccessToken
      ? (sessionAccessToken.expiresAt || 0) > (localAccessToken.expiresAt || 0)
        ? sessionAccessToken
        : localAccessToken
      : sessionAccessToken || localAccessToken
  if (mostRecentAccessToken?.expiresAt) {
    const isValidAccessToken = (mostRecentAccessToken.expiresAt || 0) > dayjs().unix()

    // Check refresh token if access token is invalid
    if (!isValidAccessToken && refreshToken?.expiresAt) {
      return (refreshToken.expiresAt || 0) > dayjs().unix()
    }

    // Token is not valid
    return isValidAccessToken
  }

  // Handle as "expired" due to expiresAt not being stored yet.
  return false
}

/**
 * Returns the authenticated user types
 * @returns {AuthenticatedUserTypes}
 */
const isAuthenticated = (): AuthenticatedUserTypes => {
  if (localStorage && sessionStorage) {
    const localIsAuth = localStorage.getItem('authenticated_user_types')
    const parsedLocalIsAuth: UserTypes[] = localIsAuth ? JSON.parse(localIsAuth) : []

    // Filter out the user types that don't have a token
    const authenticatedUserTypes = Object.values(UserTypes).filter((userType) => checkTokenIsValid(userType))

    // Return an object with the authenticated user types
    return authenticatedUserTypes.reduce(
      (prev: AuthenticatedUserTypes, curr) => {
        return {
          ...prev,
          [curr]: parsedLocalIsAuth.includes(curr)
        }
      },
      {
        [UserTypes.SALES]: false,
        [UserTypes.CUSTOMER]: false,
        [UserTypes.SUPER_USER]: false
      }
    )
  }

  return {
    [UserTypes.SALES]: false,
    [UserTypes.CUSTOMER]: false,
    [UserTypes.SUPER_USER]: false
  }
}

// SETTERS

/**
 * Sets the is_authenticated in localStorage
 *
 * @param {boolean} isAuthenticated
 * @param {UserTypes} userType
 */
const setIsAuthenticated = (isAuthenticated: boolean, userType: UserTypes) => {
  const localAuthenticatedUsers = localStorage.getItem('authenticated_user_types')
  let parsedLocalAuthenticatedUsers: UserTypes[] = localAuthenticatedUsers ? JSON.parse(localAuthenticatedUsers) : []

  if (isAuthenticated) {
    if (!parsedLocalAuthenticatedUsers.includes(userType)) parsedLocalAuthenticatedUsers.push(userType)
  } else {
    parsedLocalAuthenticatedUsers = parsedLocalAuthenticatedUsers.filter((type) => type !== userType)
  }

  localStorage.setItem('authenticated_user_types', JSON.stringify(parsedLocalAuthenticatedUsers))
}

/**
 * Sets the is_superuser in localStorage
 *
 * @param {boolean} isSuperUser
 */
const setIsSuperUser = (isSuperUser: boolean) => {
  localStorage.setItem('is_superuser', JSON.stringify(isSuperUser))
}

/**
 * Sets the tokens in localStorage
 *
 * @param {AuthTokens} tokens
 * @param {UserTypes} userType
 * @param {StorageTypes} storage
 */
const setTokens = (tokens: Partial<AuthTokens>, userType: UserTypes, storage: StorageTypes = STORAGE_TYPES.localStorage) => {
  if (storage === 'localStorage') {
    localStorage.setItem(`access_token_${userType}`, JSON.stringify(tokens.token))
    localStorage.setItem(`refresh_token_${userType}`, JSON.stringify(tokens.refreshToken))
  } else {
    sessionStorage.setItem(`access_token_${userType}`, JSON.stringify(tokens.token))
    if (tokens.refreshToken) {
      sessionStorage.setItem(`refresh_token_${userType}`, JSON.stringify(tokens.refreshToken))
    }
  }
}

// GETTERS

/**
 * Returns the access token from localStorage
 *
 * @param {UserTypes} customUserType
 * @param {StorageTypes} storage
 * @returns {Token}
 */
const getAccessToken = (customUserType?: UserTypes, storage: StorageTypes = STORAGE_TYPES.localStorage): Token | undefined => {
  const userType = customUserType || getUserType()

  if (!userType) return undefined

  const key = `access_token_${userType}`
  const token = storage === STORAGE_TYPES.localStorage ? localStorage.getItem(key) : sessionStorage.getItem(key)

  return token ? JSON.parse(token) : undefined
}

/**
 * Resolves the access token that is actually used for outgoing requests, together with the storage it came from.
 * Mirrors the header priority used in the axios & RTK Query clients (sessionStorage/magic-link token wins over localStorage/login token).
 * The source determines whether the token can be refreshed: sessionStorage (magic-link) tokens have no refresh path.
 *
 * @param {UserTypes} customUserType
 * @returns {{ token: Token; source: StorageTypes } | undefined}
 */
const getActiveAccessToken = (customUserType?: UserTypes): { token: Token; source: StorageTypes } | undefined => {
  const sessionToken = getAccessToken(customUserType, STORAGE_TYPES.sessionStorage)
  if (sessionToken) return { token: sessionToken, source: STORAGE_TYPES.sessionStorage }

  const localToken = getAccessToken(customUserType, STORAGE_TYPES.localStorage)
  return localToken ? { token: localToken, source: STORAGE_TYPES.localStorage } : undefined
}

/**
 * Returns the refresh token from localStorage
 *
 * @param {UserTypes} customUserType
 * @returns {Token}
 */
const getRefreshToken = (customUserType?: UserTypes): Token | undefined => {
  let userType = customUserType || getUserType()
  const isAuthenticatedForUserType = isAuthenticated()

  if (!userType) {
    const hasAuthenticatedUserType = Object.values(isAuthenticatedForUserType).some((isAuthenticated) => isAuthenticated)

    if (!hasAuthenticatedUserType) return undefined

    userType = identifyUserType()
    setUserType(userType)
  }

  const localStorageRefreshToken = localStorage.getItem(`refresh_token_${userType}`)

  return localStorageRefreshToken ? JSON.parse(localStorageRefreshToken) : undefined
}

export {
  isAuthenticated,

  // CLEARS
  clearTokens,
  clearSession,
  clearSessionTokens,
  clearLocalTokens,

  // SETTERS
  setIsAuthenticated,
  setIsSuperUser,
  setTokens,

  // GETTERS
  getAccessToken,
  getActiveAccessToken,
  getRefreshToken
}
